@if (!loading) {
  <div class="care-team-form-container">
    <h2 class="titleBar">
      {{
        (isEditMode ? "Edit Care Team Template" : "Create Care Team Template")
          | translate
      }}
    </h2>
    <form [formGroup]="form" (ngSubmit)="save()">
      <mat-vertical-stepper #stepper>
        <mat-step [label]="'1. ' + ('Template Information' | translate)">
          <div class="row-fields">
            <!-- Name -->
            <div>
              <div fxLayout="row" class="label-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  {{ "Name" | translate }}
                  <span class="required-asterisk">*</span>
                </div>
              </div>
              <div fxLayout="row" class="add-patient-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  <input
                    class="add-patient-input"
                    [placeholder]="'Name' | translate"
                    formControlName="name"
                  />
                </div>
              </div>
              <div fxLayout="row" class="add-patient-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  @if (form.get("name")?.touched && form.get("name")?.invalid) {
                    <div class="error-message">
                      @if (form.get("name")?.errors?.["required"]) {
                        <div>{{ "Name is required" | translate }}</div>
                      }
                      @if (form.get("name")?.errors?.["maxlength"]) {
                        <div>
                          {{ "Name cannot exceed 100 characters" | translate }}
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- Category -->
            <div>
              <div fxLayout="row" class="label-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  {{ "Category" | translate }}
                  <span class="required-asterisk">*</span>
                </div>
              </div>
              <div fxLayout="row" class="add-patient-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  <nz-select
                    formControlName="careTeamCategoryId"
                    [nzPlaceHolder]="'Category' | translate"
                    style="width: 100%"
                  >
                    @for (category of categoryOptions; track category.id) {
                      <nz-option
                        [nzValue]="category.id"
                        [nzLabel]="category.display"
                      ></nz-option>
                    }
                  </nz-select>
                </div>
              </div>
              <div fxLayout="row" class="add-patient-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  @if (
                    form.get("careTeamCategoryId")?.touched &&
                    form.get("careTeamCategoryId")?.invalid
                  ) {
                    <div class="error-message">
                      {{ "Category is required" | translate }}
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- Note -->
            <div>
              <div fxLayout="row" class="label-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  {{ "Note" | translate }}
                </div>
              </div>
              <div fxLayout="row" class="add-patient-row">
                <div fxLayout="col" fxFlex="100" class="add-patient-col">
                  <textarea
                    class="add-patient-input care-team-note"
                    [placeholder]="'Note' | translate"
                    formControlName="note"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation -->
          <div fxLayout="row" fxLayoutAlign="end center" class="form-actions">
            <button
              type="button"
              class="add-patient-button"
              (click)="nextStep(stepper, 1)"
            >
              {{ "Next" | translate }}
            </button>
          </div>
        </mat-step>

        <mat-step [label]="'2. ' + ('Care Team Members' | translate)">
          <!-- Care Team Members -->
          <div class="row-fields care-team-members-section">
            <div
              fxLayout="row"
              fxLayoutAlign="space-between center"
              class="label-row"
            >
              <div class="add-patient-col">
                {{ "Care Team Members" | translate }}
                <span class="required-asterisk">*</span>
              </div>
              @if (!showAddMemberPanel) {
                <button
                  type="button"
                  class="add-member-button"
                  (click)="openAddMemberPanel()"
                >
                  <i class="fa-solid fa-plus"></i>
                  {{ "Add Member" | translate }}
                </button>
              }
            </div>

            @if (participants.length === 0) {
              <div class="empty-state">
                <i class="fa-regular fa-address-card empty-state-icon"></i>
                <div class="empty-state-text">
                  {{ "No care team members added yet" | translate }}
                </div>
              </div>
            }

            <ng-template #memberFormFields>
              <div
                [formGroup]="pendingParticipant"
                class="add-member-panel-content"
              >
                <div
                  fxLayout="row"
                  fxLayoutGap="1.5em"
                  class="member-tab-header"
                >
                  <div
                    class="member-tab"
                    [class.member-tab--active]="activeMemberTab === 'internal'"
                    (click)="selectMemberTab('internal')"
                  >
                    {{ "Internal user" | translate }}
                  </div>
                  <div
                    class="member-tab"
                    [class.member-tab--active]="activeMemberTab === 'external'"
                    (click)="selectMemberTab('external')"
                  >
                    {{ "External / CyMA doctor" | translate }}
                  </div>
                </div>

                @if (activeMemberTab === "external") {
                  <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="0.75em"
                    class="cyma-toggle-row"
                  >
                    <label>{{ "Search from CyMa list" | translate }}</label>
                    <nz-switch
                      [ngModel]="useCymaList"
                      [ngModelOptions]="{ standalone: true }"
                      (ngModelChange)="toggleCymaList($event)"
                    ></nz-switch>
                  </div>
                }

                <div
                  fxLayout="row"
                  fxLayout.lt-md="column"
                  fxLayoutAlign="start center"
                  fxLayoutAlign.lt-md="start stretch"
                  fxLayoutGap="1em"
                >
                  @if (activeMemberTab === "internal") {
                    <div fxFlex="30" fxFlex.lt-md="100" class="add-patient-col">
                      <nz-select
                        formControlName="userId"
                        nzShowSearch
                        [nzPlaceHolder]="'User' | translate"
                        style="width: 100%"
                      >
                        @for (user of userOptions; track user.id) {
                          <nz-option
                            [nzValue]="user.id"
                            [nzLabel]="user.displayName"
                          ></nz-option>
                        }
                      </nz-select>
                      @if (
                        pendingParticipant.get("userId")?.touched &&
                        !pendingParticipant.get("userId")?.value
                      ) {
                        <div class="error-message">
                          {{ "User is required" | translate }}
                        </div>
                      }
                    </div>
                  } @else if (useCymaList) {
                    <div fxFlex="30" fxFlex.lt-md="100" class="add-patient-col">
                      <nz-select
                        formControlName="externalDoctorCymaId"
                        nzShowSearch
                        [nzPlaceHolder]="'External doctor' | translate"
                        style="width: 100%"
                      >
                        @for (
                          doctor of externalDoctorCymaOptions;
                          track doctor.id
                        ) {
                          <nz-option
                            [nzValue]="doctor.id"
                            [nzLabel]="doctor.firstName + ' ' + doctor.lastName"
                          ></nz-option>
                        }
                      </nz-select>
                      @if (
                        pendingParticipant.get("externalDoctorCymaId")
                          ?.touched &&
                        !pendingParticipant.get("externalDoctorCymaId")?.value
                      ) {
                        <div class="error-message">
                          {{ "External doctor is required" | translate }}
                        </div>
                      }
                    </div>
                  } @else {
                    <div fxFlex="30" fxFlex.lt-md="100" class="add-patient-col">
                      <nz-select
                        formControlName="externalDoctorId"
                        nzShowSearch
                        [nzPlaceHolder]="'External doctor' | translate"
                        style="width: 100%"
                      >
                        @for (
                          doctor of externalDoctorOptions;
                          track doctor.id
                        ) {
                          <nz-option
                            [nzValue]="doctor.id"
                            [nzLabel]="doctor.firstName + ' ' + doctor.lastName"
                          ></nz-option>
                        }
                      </nz-select>
                      @if (
                        pendingParticipant.get("externalDoctorId")?.touched &&
                        !pendingParticipant.get("externalDoctorId")?.value
                      ) {
                        <div class="error-message">
                          {{ "External doctor is required" | translate }}
                        </div>
                      }
                    </div>
                  }
                  <div fxFlex="40" fxFlex.lt-md="100" class="add-patient-col">
                    <nz-select
                      formControlName="careTeamRoleId"
                      nzShowSearch
                      [nzPlaceHolder]="'Role' | translate"
                      style="width: 100%"
                    >
                      @for (role of roleOptions; track role.id) {
                        <nz-option
                          [nzValue]="role.id"
                          [nzLabel]="role.display"
                        ></nz-option>
                      }
                    </nz-select>
                    @if (
                      pendingParticipant.get("careTeamRoleId")?.touched &&
                      pendingParticipant.get("careTeamRoleId")?.invalid
                    ) {
                      <div class="error-message">
                        {{ "Role is required" | translate }}
                      </div>
                    }
                  </div>
                  <div fxFlex="20" fxFlex.lt-md="100" class="add-patient-col">
                    <input
                      type="number"
                      class="add-patient-input"
                      min="1"
                      [placeholder]="'Display Order' | translate"
                      formControlName="displayOrder"
                    />
                    @if (
                      pendingParticipant.get("displayOrder")?.touched &&
                      pendingParticipant.get("displayOrder")?.invalid
                    ) {
                      <div class="error-message">
                        @if (
                          pendingParticipant.get("displayOrder")?.errors?.[
                            "required"
                          ]
                        ) {
                          <div>{{ "Display order is required" | translate }}</div>
                        }
                        @if (
                          pendingParticipant.get("displayOrder")?.errors?.["min"]
                        ) {
                          <div>
                            {{ "Display order must be greater than 0" | translate }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
                <div
                  fxLayout="row"
                  fxLayoutAlign="end center"
                  fxLayoutGap="0.75em"
                  class="add-member-panel-actions"
                >
                  <button
                    type="button"
                    class="cancel-button"
                    (click)="cancelAddMember()"
                  >
                    {{ "Cancel" | translate }}
                  </button>
                  <button
                    type="button"
                    class="add-patient-button"
                    (click)="confirmAddMember()"
                  >
                    {{ "Confirm" | translate }}
                  </button>
                </div>
              </div>
            </ng-template>

            @for (i of sortedParticipantIndices(); track i) {
              @if (showAddMemberPanel && editingParticipantIndex === i) {
                <div class="add-member-panel open">
                  <ng-container
                    [ngTemplateOutlet]="memberFormFields"
                  ></ng-container>
                </div>
              } @else {
                @let memberValue = participants.at(i).value;
                @let memberName = getParticipantDisplayName(memberValue);
                <div
                  fxLayout="row"
                  fxLayoutAlign="space-between center"
                  fxLayoutGap="1em"
                  class="member-card"
                  [class.member-card--new]="!memberValue.id"
                >
                  <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="1em"
                    class="member-card-info"
                  >
                    <div class="member-avatar">
                      {{ getInitials(memberName) }}
                    </div>
                    <div class="member-details">
                      <div class="member-name">{{ memberName }}</div>
                      <div class="member-subtitle">
                        {{ getRoleDisplay(memberValue.careTeamRoleId) }}
                      </div>
                    </div>
                  </div>
                  <div
                    fxLayout="row"
                    fxLayoutAlign="end center"
                    fxLayoutGap="0.75em"
                  >
                    @if (memberValue.displayOrder === 1) {
                      <div class="member-external-badge">
                        {{ "Team Lead" | translate }}
                      </div>
                    }
                    @if (
                      memberValue.externalDoctorId ||
                      memberValue.externalDoctorCymaId
                    ) {
                      <div class="member-external-badge">
                        {{
                          memberValue.externalDoctorCymaId
                            ? ("CyMA" | translate)
                            : ("External" | translate)
                        }}
                      </div>
                    }
                    @if (memberValue.displayOrder) {
                      <div class="member-order-badge">
                        #{{ memberValue.displayOrder }}
                      </div>
                    }
                    <button
                      type="button"
                      class="edit-member-button"
                      [title]="'Edit' | translate"
                      [disabled]="showAddMemberPanel"
                      (click)="openEditMemberPanel(i)"
                    >
                      <i class="fa-regular fa-pen-to-square"></i>
                    </button>
                    <button
                      type="button"
                      class="remove-member-button"
                      [title]="'Remove' | translate"
                      [disabled]="showAddMemberPanel"
                      (click)="removeParticipant(i)"
                    >
                      <i class="fa-regular fa-trash-can"></i>
                    </button>
                  </div>
                </div>
              }
            }

            @if (showAddMemberPanel && editingParticipantIndex === null) {
              <div #addMemberPanel class="add-member-panel open">
                <ng-container
                  [ngTemplateOutlet]="memberFormFields"
                ></ng-container>
              </div>
            }
          </div>

          <!-- Actions -->
          <div
            fxLayout="row"
            fxLayoutAlign="space-between center"
            class="form-actions"
          >
            <button
              type="button"
              class="cancel-button"
              [disabled]="showAddMemberPanel"
              (click)="cancel()"
            >
              {{ "Cancel" | translate }}
            </button>
            <div fxLayout="row" fxLayoutAlign="end center">
              <button
                type="button"
                class="back-button"
                [disabled]="showAddMemberPanel"
                (click)="nextStep(stepper, -1)"
              >
                {{ "Back" | translate }}
              </button>
              <button
                type="submit"
                class="add-patient-button btn-margin"
                [disabled]="saving || showAddMemberPanel"
              >
                {{
                  (isEditMode ? "Save Changes" : "Create Template") | translate
                }}
              </button>
            </div>
          </div>
        </mat-step>
      </mat-vertical-stepper>
    </form>
  </div>
}

@if (loading) {
  <div class="loading-container">
    <div class="loading-text">
      <p>{{ "Loading..." | translate }}</p>
    </div>
    <mat-spinner></mat-spinner>
  </div>
}
